// client/src/utils/estadoServicio.js
// ─────────────────────────────────────────────────────────────
// Estado que se DEBE MOSTRAR, calculado al vuelo (misma lógica que el backend).
// Solo el 'confirmado' avanza por tiempo:
//   confirmado → (llega inicio) → en_curso → (pasa el fin) → realizado
// El resto de estados se muestran tal cual.
//
// Tiempos:
//   inicio = fecha + hora_salida (00:00 si no hay hora)
//   fin    = (fecha_regreso || fecha) + hora_llegada
//            si no hay hora_llegada → 23:59:59 del día de fin
// ─────────────────────────────────────────────────────────────

import { esDeMadrugada, hhmm as hhmmR } from './regresos';

function soloFecha(f) {
  if (!f) return null;
  return String(f).slice(0, 10);
}

function instanteInicio(s) {
  const fecha = soloFecha(s.fecha);
  if (!fecha) return null;
  let hora = s.hora_salida ? String(s.hora_salida).slice(0, 8) : '00:00:00';
  if (hora.length === 5) hora += ':00';
  return new Date(`${fecha}T${hora}`);
}

function instanteFin(s) {
  const fechaFin = soloFecha(s.fecha_regreso) || soloFecha(s.fecha);
  if (!fechaFin) return null;
  let fin;
  if (s.hora_llegada) {
    let hora = String(s.hora_llegada).slice(0, 8);
    if (hora.length === 5) hora += ':00';
    fin = new Date(`${fechaFin}T${hora}`);
    // REG después de medianoche SIN fecha de regreso (sale 20:00, REG 01:00):
    // ese REG es del día siguiente. Sin esto el servicio se daba por
    // realizado a la 01:00 de su propio día, antes de salir (entrega 285).
    // Misma regla que en el servidor y en el cierre nocturno.
    if (!soloFecha(s.fecha_regreso) && s.hora_salida && hora < String(s.hora_salida).slice(0, 8)) {
      fin.setDate(fin.getDate() + 1);
    }
  } else {
    fin = new Date(`${fechaFin}T23:59:59`);
  }
  // Boda: no termina hasta su ÚLTIMO regreso, aunque sea de madrugada. Si
  // no, a la 1 del día siguiente saldría como realizada con el coche todavía
  // trabajando. Misma regla que en el servidor.
  const fecha = soloFecha(s.fecha);
  for (const r of (s.regresos || [])) {
    const h = hhmmR(r.hora);
    if (!h || !fecha) continue;
    const d = new Date(`${fecha}T${h}:00`);
    if (esDeMadrugada(h, s.hora_salida)) d.setDate(d.getDate() + 1);
    if (d > fin) fin = d;
  }
  return fin;
}

// Devuelve { estado, auto } — auto=true si lo ha decidido el reloj (no el usuario).
//
// Estado virtual 'asignado': un servicio CONFIRMADO que ya tiene conductor
// asignado en Tráfico se muestra como 'asignado'. No se guarda en BD: se
// calcula a partir de si el servicio trae conductor/asignación, así nunca
// puede descuadrarse respecto a la tabla de asignaciones.
//
// IMPORTANTE: el avance por reloj (en_curso / realizado) SOLO se aplica si el
// servicio tiene conductor asignado. Un servicio sin asignar no puede estar
// "en curso": si nadie lo conduce, no ha empezado. Sin asignación se queda en
// 'confirmado' aunque su hora ya haya pasado, para que siga visible como
// pendiente de asignar en Tráfico.
export function estadoEfectivoInfo(s, ahora = new Date()) {
  const base = s.estado;
  if (base !== 'confirmado') return { estado: base, auto: false };

  const asignado = tieneAsignacion(s);
  // Sin conductor: nunca avanza por tiempo.
  if (!asignado) return { estado: 'confirmado', auto: false };

  const inicio = instanteInicio(s);
  const fin    = instanteFin(s);
  if (!inicio || !fin) return { estado: 'asignado', auto: false };

  if (ahora >= fin)    return { estado: 'realizado', auto: true };
  if (ahora >= inicio) return { estado: 'en_curso',  auto: true };
  // Ya tiene conductor pero aún no ha empezado
  return { estado: 'asignado', auto: false };
}

// ¿El servicio tiene CONDUCTOR asignado? Acepta las distintas formas en que
// llega el dato según la pantalla (agenda, tráfico, listados).
//
// Conductor, no coche (entrega 243). Antes bastaba con `asignacion_id`, y
// desde la entrega 238 un coche metido en una caja TAMBIÉN tiene fila de
// asignación, con `trabajador_id` vacío. El resultado era que un servicio
// aparcado en una caja, sin nadie que lo condujera, se pintaba como
// "realizado" en cuanto pasaba su hora en Agenda y en Tráfico, mientras el
// servidor lo dejaba en 'confirmado' porque él sí exige conductor. De ahí
// que el mismo viaje saliera realizado para uno y no para otro.
function tieneAsignacion(s) {
  // Dado a un COLABORADOR (entrega 279): lo hace otra empresa, pero está
  // cubierto. Cuenta como asignado, igual que en el servidor; si no, se
  // quedaría en "aceptado" para siempre.
  if (s.colaborador_id) return true;
  if (s.trabajador_id) return true;
  if (s.conductores && String(s.conductores).trim()) return true;
  if (Array.isArray(s.asignaciones) && s.asignaciones.some(
    a => a && (a.trabajador_id || a.conductor || a.conductor_nombre))) return true;
  return false;
}

// Atajo: solo el nombre del estado a mostrar.
export function estadoEfectivo(s, ahora = new Date()) {
  return estadoEfectivoInfo(s, ahora).estado;
}

// ─────────────────────────────────────────────────────────────
//  CÓMO SE PINTA Y CÓMO SE LLAMA — un solo sitio (entrega 243)
//
//  Había cuatro tablas de colores repartidas por el proyecto (Agenda,
//  Tráfico, Mis Servicios e Histórico) y una tabla de nombres que solo
//  tenía la Agenda. El mismo viaje terminado salía con una chapa verde
//  oscura y la palabra "realizado" en la Agenda, con un ✓ gris de diez
//  píxeles en Tráfico, con otra chapa de otro verde en Mis Servicios y
//  tachado al imprimir. Por eso a unos "les aparece realizado" y a otros
//  no: no dependía de quién fuera cada uno, sino de en qué pantalla
//  estuviera mirando.
//
//  Desde aquí lo decide una sola tabla. Si mañana hay que cambiar un
//  color o un nombre, se cambia una vez.
//
//  ─── Y DESDE LA ENTREGA 278, LOS COLORES LOS DECIDE LA OFICINA ───
//
//  Esta tabla ya no está escrita a fuego: lo que hay aquí abajo son LOS
//  COLORES DE SIEMPRE, los que se usan mientras el servidor no ha
//  contestado (o si no contesta). Al entrar en la aplicación, el contexto
//  `ColoresEstadoContext` pide `GET /api/estados-color` y llama a
//  `aplicarColoresEstado()`, que PISA las entradas de esta misma tabla.
//
//  POR QUÉ SE PISA EL OBJETO EN VEZ DE CREAR OTRO: `ESTADO_COLOR` lo
//  importan veinte sitios (Agenda, Tráfico, Histórico, Mis servicios, la
//  ficha del servicio…) y todos leen `ESTADO_COLOR[estado].bg` en el
//  momento de pintar. Cambiando lo que hay DENTRO del objeto, los veinte
//  cogen los colores nuevos sin tocar ninguno. El menú (AppLayout) espera a
//  que estén cargados antes de montar la pantalla, así que ninguna llega a
//  pintarse con los de siempre y cambiar de golpe.
// ─────────────────────────────────────────────────────────────
//
//  `linea` (entrega 280): el color de la letra de TODA LA LÍNEA del
//  servicio en la Agenda (la fila de la tabla o la tarjeta del móvil). ''
//  = letra normal. Lo de siempre: el pendiente entero en verde RG y el
//  cancelado entero en rojo, que antes iban escritos en un <style> de la
//  Agenda. Ahora se elige en Colores de estados, para cualquier estado.
export const ESTADO_COLOR = {
  pendiente:  { bg:'#FEF3C7', color:'#92400E', border:'#FCD34D', linea:'#0D5D3A' },
  confirmado: { bg:'#DBEAFE', color:'#1E40AF', border:'#93C5FD', linea:'' },
  asignado:   { bg:'#EDE9FE', color:'#5B21B6', border:'#C4B5FD', linea:'' },
  en_curso:   { bg:'#D1FAE5', color:'#065F46', border:'#6EE7B7', linea:'' },
  realizado:  { bg:'#14532D', color:'#DCFCE7', border:'#166534', linea:'' },
  cancelado:  { bg:'#FEE2E2', color:'#7F1D1D', border:'#FCA5A5', linea:'#B91C1C' },
};

// Los estados de un PRESUPUESTO son otra lista (aquí "aceptado" es un
// estado de verdad, no el nombre de pantalla de 'confirmado'). Hasta la 277
// se pintaban con las clases .badge-gray / .badge-green / .badge-red de
// index.css; estos son esos mismos colores, pasados a número.
export const PRESUPUESTO_COLOR = {
  pendiente: { bg:'#F3F4F6', color:'#4B5563', border:'#F3F4F6', linea:'' },
  aceptado:  { bg:'#E8F5EE', color:'#094D30', border:'#E8F5EE', linea:'' },
  rechazado: { bg:'#FEF2F2', color:'#DC2626', border:'#FEF2F2', linea:'' },
  cancelado: { bg:'#FEF2F2', color:'#DC2626', border:'#FEF2F2', linea:'' },
};

// ─── LAS CHAPAS DE TRANSFER, REFUERZO, REGRESO… (entrega 308) ───────
// Lo que pidió Manuel: "me gustaría poder elegir los colores de esos que
// ponen transfer, refuerzo, etc.". Estaban escritos en cada pantalla (el
// morado del transfer, el naranja del refuerzo…). Ahora viven aquí, con
// los colores de siempre, y Colores de estados los pisa igual que los de
// los estados (ámbito 'chapa' de la misma tabla `estados_color`).
// Taller, descanso, nota… NO están aquí: esos ya se eligen en Tipos de
// ausencia y Tipos de otro trabajo.
export const CHAPA_COLOR = {
  transfer:     { bg:'#6D28D9', color:'#FFFFFF', border:'#6D28D9', linea:'' },  // 🔄 TRANSFER: vuelve de vacío
  refuerzo:     { bg:'#EA580C', color:'#FFFFFF', border:'#EA580C', linea:'' },  // 🔁 REFUERZO: van los dos en el coche
  trae_regreso: { bg:'#1D4ED8', color:'#FFFFFF', border:'#1D4ED8', linea:'' },  // ↩ REGRESO: el que se trae el coche
  coche_n:      { bg:'#1D4ED8', color:'#FFFFFF', border:'#1D4ED8', linea:'' },  // 1/2: coche de un servicio de varios
  varios_dias:  { bg:'#7C3AED', color:'#FFFFFF', border:'#7C3AED', linea:'' },  // REG-02/11 · ↩ REGRESO · día 1 de 3
  regresos:     { bg:'#6D28D9', color:'#FFFFFF', border:'#6D28D9', linea:'' },  // 🌙 REGRESOS de madrugada · ↩ horas
};
// Los dos colores de una chapa, listos para meter en un `style`.
export function chapaColor(clave) {
  const c = CHAPA_COLOR[clave] || CHAPA_COLOR.transfer;
  return { background: c.bg, color: c.color };
}

// Copia congelada de los de siempre, para saber a qué se vuelve con
// «Restaurar» y para la prueba que comprueba que coinciden con los que
// siembra el SQL (server/utils/estadosColor.js).
function congelarTabla(t) {
  const c = {};
  for (const k of Object.keys(t)) c[k] = Object.freeze({ ...t[k] });
  return Object.freeze(c);
}
export const ESTADO_COLOR_DE_SIEMPRE      = congelarTabla(ESTADO_COLOR);
export const PRESUPUESTO_COLOR_DE_SIEMPRE = congelarTabla(PRESUPUESTO_COLOR);
export const CHAPA_COLOR_DE_SIEMPRE       = congelarTabla(CHAPA_COLOR);

const TABLA_POR_AMBITO = { servicio: ESTADO_COLOR, presupuesto: PRESUPUESTO_COLOR, chapa: CHAPA_COLOR };

/**
 * Pisa las tablas de colores con las filas que manda el servidor
 * (`GET /api/estados-color` → [{ ambito, estado, color_fondo, color_letra,
 * color_borde }]). Devuelve cuántas entradas ha cambiado.
 *
 * Solo toca estados que la pantalla conoce: una fila con un estado que no
 * esté en la tabla no crea entrada nueva (no habría quien la pintara).
 */
export function aplicarColoresEstado(filas) {
  let n = 0;
  for (const f of (Array.isArray(filas) ? filas : [])) {
    const tabla = TABLA_POR_AMBITO[f && f.ambito];
    if (!tabla || !tabla[f.estado]) continue;
    if (!esHex(f.color_fondo) || !esHex(f.color_letra)) continue;
    tabla[f.estado] = {
      bg:     f.color_fondo,
      color:  f.color_letra,
      border: esHex(f.color_borde) ? f.color_borde : f.color_fondo,
      // La línea (entrega 280): solo si es un color de verdad; '' o NULL = normal.
      linea:  esHex(f.color_linea) ? f.color_linea : '',
    };
    n++;
  }
  return n;
}

// ─── La letra de toda la línea, en la Agenda (entrega 280) ─────────
//
// La Agenda pinta cada servicio con la clase `fila-<estado>` (tabla) o
// `tarjeta-<estado>` (móvil y lista), y este CSS es el que le da color a
// la línea entera. Va con `*` y `!important` porque cada celda pone su
// propio color (grises, distintivos) y el color del padre no los tapa.
// Solo salen reglas para los estados que tienen color de línea.
export function claseLinea(prefijo, estado) {
  return `${prefijo}-${String(estado || '').replace(/[^a-z_]/gi, '')}`;
}
//
// OJO (entrega 308): la línea pinta el TEXTO de la fila, no las chapas. Antes
// el `*` pillaba también la chapa del estado, la del transfer, la del
// colaborador…, y les cambiaba la letra: con la línea de «realizado» en
// negro, la chapa verde oscura de realizado salía con la letra negra en vez
// de la blanca que tiene configurada. Ahora se salta todo lo que lleva fondo
// propio dentro de la fila (y lo que hay dentro de ello).
export function cssLineasEstado() {
  const regla = (sel) => `${sel}, ${sel} *:not([style*="background"]):not(${sel} [style*="background"] *)`;
  return Object.keys(ESTADO_COLOR)
    .filter(e => esHex(ESTADO_COLOR[e].linea))
    .map(e => `${regla(`tr.${claseLinea('fila', e)}`)}, ${regla(`.${claseLinea('tarjeta', e)}`)} { color: ${ESTADO_COLOR[e].linea} !important; }`)
    .join('\n');
}

function esHex(v) { return typeof v === 'string' && /^#[0-9A-Fa-f]{6}$/.test(v); }

// Los dos colores de un estado de presupuesto, listos para un `style`.
// El de la chapa de servicio ya existe: colorEstado(), más abajo.
export function chapaPresupuesto(estado) {
  const c = PRESUPUESTO_COLOR[estado] || PRESUPUESTO_COLOR.pendiente;
  return { background: c.bg, color: c.color };
}

// Cómo se LLAMA cada estado en pantalla.
//
// En la base de datos el estado sigue siendo 'confirmado' y así se guarda,
// se filtra y lo entienden Tráfico y el resto de pantallas: aquí solo se
// cambia el nombre que se muestra. Tocar el valor real obligaría a migrar
// la tabla y a revisar todas las comparaciones del proyecto.
const ESTADO_LABEL = {
  confirmado: 'aceptado',
  // 'asignado' es un aceptado que YA tiene conductor puesto en Tráfico. Se
  // llamaba solo "asignado" y parecía otra cosa distinta de "aceptado", así
  // que al filtrar y al leer la lista descuadraba. Ahora se dice lo que es:
  // aceptado, con la coletilla de que además está repartido.
  asignado: 'aceptado · asignado',
};

export function etiquetaEstado(e) {
  if (!e) return '';
  return ESTADO_LABEL[e] || String(e).replace('_', ' ');
}

// Colores del estado que TOCA MOSTRAR (no el crudo de la tabla).
export function colorEstado(s, ahora = new Date()) {
  const e = typeof s === 'string' ? s : estadoEfectivo(s, ahora);
  return ESTADO_COLOR[e] || ESTADO_COLOR.pendiente;
}
